import { useCallback, useEffect, useState } from "react";

import { PageHead } from "./Shell";
import { formatDate } from "./Records";
import { AdminError, admin, type PanelUserRow } from "./api";

const ROLE_LABEL: Record<string, string> = {
  admin: "Konsultant",
  superadmin: "Superadministrator",
};

const ROLE_HINT: Record<string, string> = {
  admin: "prowadzi sprawy kandydatów",
  superadmin: "dodatkowo nadaje i odbiera dostępy",
};

/**
 * Kto ma wejście do panelu.
 *
 * Wcześniej lista adresów siedziała w zmiennej środowiskowej: każdy nowy
 * konsultant wymagał wdrożenia po naszej stronie, a ludzie w tej pracy
 * przychodzą i odchodzą co miesiąc. Teraz dopisuje ich ten, kto za to odpowiada.
 */
export function Users() {
  const [rows, setRows] = useState<PanelUserRow[]>([]);
  const [fromEnv, setFromEnv] = useState<string[]>([]);
  const [you, setYou] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await admin.users();
      setRows(data.items);
      setFromEnv(data.from_env);
      setYou(data.you);
      setError(null);
    } catch (caught) {
      setError((caught as AdminError).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async () => {
    setBusy(true);
    try {
      await admin.addUser(email.trim(), role);
      setEmail("");
      await load();
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: PanelUserRow) => {
    if (!window.confirm(`Odebrać dostęp ${row.email}? Zadziała od razu.`)) return;
    setBusy(true);
    try {
      await admin.removeUser(row.email);
      await load();
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="adm__section">
      <PageHead
        title="Dostępy do panelu"
        subtitle="Kto może otwierać teczki kandydatów — zmiany działają od razu, bez czekania na wygaśnięcie sesji"
      />

      <div className="adm__toolbar">
        <input
          className="input"
          type="email"
          placeholder="adres@firma.pl"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && email.trim()) void add();
          }}
        />
        <select className="select" value={role} onChange={(event) => setRole(event.target.value)}>
          {Object.entries(ROLE_LABEL).map(([id, label]) => (
            <option key={id} value={id}>
              {label} — {ROLE_HINT[id]}
            </option>
          ))}
        </select>
        <button className="btn btn--primary" disabled={busy || !email.trim()} onClick={add}>
          Dodaj dostęp
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="adm__tablewrap">
        <table className="adm__table">
          <thead>
            <tr>
              <th>Adres</th>
              <th>Rola</th>
              <th>Dodał</th>
              <th>Ostatnie wejście</th>
              <th />
            </tr>
          </thead>
          {/* Kaskada z tokens.css — lista dostępów wchodzi kolejno. */}
          <tbody className="kaskada">
            {rows.map((row, index) => (
              <tr key={row.email} style={{ "--i": index } as React.CSSProperties}>
                <td>
                  {row.email}
                  {row.email === you && <span className="pill pill--checked">to Ty</span>}
                </td>
                <td>
                  {/* Zmiana roli to ta sama operacja co dodanie — bez osobnego
                      ekranu i bez kasowania dostępu po drodze. */}
                  <select
                    className="select select--tight"
                    value={row.role}
                    disabled={busy}
                    onChange={async (event) => {
                      setBusy(true);
                      try {
                        await admin.addUser(row.email, event.target.value);
                        await load();
                      } catch (caught) {
                        setError((caught as AdminError).message);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {Object.entries(ROLE_LABEL).map(([id, label]) => (
                      <option key={id} value={id}>
                        {label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="adm__muted">{row.added_by ?? "—"}</td>
                {/* Jeden format daty w całym panelu — pełny toLocaleString
                    dokładał sekundy, których nikt tu nie porównuje. */}
                <td className="adm__muted adm__date">
                  {row.last_login_at ? formatDate(row.last_login_at) : "jeszcze nigdy"}
                </td>
                <td className="adm__right">
                  {/* Odebranie dostępu działa od razu — akcja niszcząca mówi
                      maliną w tekście, nie pełnym wypełnieniem. */}
                  <button className="btn btn--ghost adm__risky" disabled={busy} onClick={() => remove(row)}>
                    Odbierz
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="adm__muted">
                  Nikogo tu jeszcze nie ma — wejście działa wyłącznie przez adresy z konfiguracji.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Adresy z konfiguracji: są, działają i nie da się ich stąd usunąć.
          To wyjście awaryjne na wypadek pomyłki w prawach — lepiej o nim
          wiedzieć, niż odkryć je przy próbie odebrania sobie dostępu. */}
      {fromEnv.length > 0 && (
        <p className="adm__muted">
          Zawsze wpuszczane (z konfiguracji serwera, pełne prawa): {fromEnv.join(", ")}. Tych
          wpisów nie da się zmienić z panelu — to zapasowe wejście, gdyby dostępy zostały
          nadane błędnie.
        </p>
      )}
    </section>
  );
}
